import { useSyncLogHistory, type SyncSource, type DBSyncLog } from "@/hooks/use-sync-coverage";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<DBSyncLog["status"], string> = {
  running: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  error: "bg-red-100 text-red-700",
};

const STATUS_LABELS: Record<DBSyncLog["status"], string> = {
  running: "Kjører",
  success: "OK",
  error: "Feilet",
};

interface Props {
  source?: SyncSource;
  entity_type?: string;
  limit?: number;
}

/**
 * Tabellvisning av sync_log-rader. Filtrerbar per source/entity_type.
 * Stilen speiler `ImportLog.tsx` for konsistens.
 */
export function SyncLogTable({ source, entity_type, limit = 50 }: Props) {
  const { data: rows = [], isLoading } = useSyncLogHistory({
    source,
    entity_type,
    limit,
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Henter historikk…</p>;
  }
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground italic">Ingen kjøringer ennå.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-2 px-2 font-medium">Når</th>
            <th className="text-left py-2 px-2 font-medium">Type</th>
            <th className="text-left py-2 px-2 font-medium">Status</th>
            <th className="text-left py-2 px-2 font-medium">Trigger</th>
            <th className="text-right py-2 px-2 font-medium">Records</th>
            <th className="text-right py-2 px-2 font-medium">Varighet</th>
            <th className="text-left py-2 px-2 font-medium">Dekning / feil</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <SyncLogRow key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SyncLogRow({ row }: { row: DBSyncLog }) {
  const startedAt = new Date(row.started_at);
  const completedAt = row.completed_at ? new Date(row.completed_at) : null;
  const durationMs = completedAt ? completedAt.getTime() - startedAt.getTime() : null;

  const triggerLabel: Record<string, string> = {
    cron: "Cron",
    manual: "Manuell",
    backfill: "Backfill",
    unknown: "—",
  };
  const triggeredBy = row.metadata?.triggered_by ?? "unknown";

  const coverageText = describeCoverage(row);

  return (
    <tr className="border-b border-border/40">
      <td className="py-2 px-2 tabular-nums text-muted-foreground whitespace-nowrap">
        {startedAt.toLocaleString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </td>
      <td className="py-2 px-2 whitespace-nowrap">
        <span className="text-xs">
          {row.source === "poweroffice" ? "PO" : "Monday"} · {row.entity_type}
        </span>
      </td>
      <td className="py-2 px-2">
        <span
          className={cn(
            "px-1.5 py-0.5 rounded text-[10px] font-medium",
            STATUS_STYLES[row.status],
          )}
        >
          {STATUS_LABELS[row.status]}
        </span>
      </td>
      <td className="py-2 px-2 text-xs text-muted-foreground">
        {triggerLabel[triggeredBy]}
      </td>
      <td className="py-2 px-2 text-right tabular-nums">{row.records_synced}</td>
      <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">
        {durationMs != null ? `${(durationMs / 1000).toFixed(1)}s` : "—"}
      </td>
      <td className="py-2 px-2 text-xs text-muted-foreground max-w-[320px] truncate" title={coverageText}>
        {row.status === "error" && row.error_message
          ? row.error_message
          : coverageText}
      </td>
    </tr>
  );
}

function describeCoverage(row: DBSyncLog): string {
  const m = row.metadata ?? {};
  const parts: string[] = [];
  if (m.from_date && m.to_date) {
    parts.push(`${m.from_date} → ${m.to_date}`);
  }
  if (Array.isArray(m.project_codes)) {
    parts.push(`${m.project_codes.length} prosjekter`);
  }
  if (Array.isArray(m.user_ids)) {
    parts.push(`${m.user_ids.length} brukere`);
  }
  if (Array.isArray(m.project_ids)) {
    parts.push(`${m.project_ids.length} prosjekter`);
  }
  if (typeof m.project_count === "number") {
    parts.push(`${m.project_count} prosjekter`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}
